<template>
  <modal-component
    :is-displayed="isCreateModalOpened"
    :no-padding="true"
    :title="$t('WorkOrders.CreateTitle')"
    @toggle-modal="$emit('toggle-modal')"
  >
    <form-wrapper :is-form-in-modal="true">
      <form @submit.prevent="handleSubmit">
        <form-divider>
          <div class="relative items-center py-4">
            <label class="whitespace-nowrap text-sm font-medium not-italic text-gray-800">{{
              $t("WorkOrders.User")
            }}</label>
            <div class="flex items-center justify-center">
              <small-loader v-if="isUserListLoading" height="h-8" width="w-8" />
              <v-select
                v-else
                v-model="workOrder.appliedToUserId"
                :class="`mt-1 w-full`"
                :options="users"
                :reduce="(user : any) => user.id"
                label="email"
              ></v-select>
            </div>
          </div>
          <input-field v-model="workOrder.pieces" :label="$t('WorkOrders.Pieces')" />
          <input-field v-model="workOrder.comment" :label="$t('WorkOrders.Comment')" />
        </form-divider>
        <button-wrapper :is-submit-loading="isSubmitLoading" />
      </form>
    </form-wrapper>
  </modal-component>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import ModalComponent from "@/core/Components/Modal.vue";
import FormWrapper from "@/core/Components/FormElements/FormWrapper.vue";
import FormDivider from "@/core/Components/FormElements/FormDivider.vue";
import ButtonWrapper from "@/core/Components/FormElements/ButtonWrapper.vue";
import SmallLoader from "@/core/Components/SmallLoader.vue";
import VSelect from "vue-select";
import InputField from "@/core/Components/FormElements/InputField.vue";
import { ErrorMessageFormatter } from "@/core/Services/GlobalFunctions";
import { useToast } from "vue-toastification";
import WorkOrderService from "@/modules/WorkOrders/Services/WorkOrderService";
import UserService, { User } from "@/modules/Users/Services/UserService";
import { useUserStore } from "@/core/Store/userStore";

export default defineComponent({
  components: {
    InputField,
    VSelect,
    SmallLoader,
    ButtonWrapper,
    FormDivider,
    FormWrapper,
    ModalComponent,
  },
  props: {
    isCreateModalOpened: Boolean,
    refreshData: {
      type: Function,
      required: true,
    },
  },
  emits: ["toggle-modal"],
  data() {
    return {
      toast: useToast(),
      userService: new UserService(),
      workOrderService: new WorkOrderService(),
      userStore: useUserStore(),
      users: [] as User[],
      isUserListLoading: false,
      isLoading: false,
      isSubmitLoading: false,
      workOrder: {
        companyId: 0,
        appliedToUserId: null,
        pieces: 0,
        comment: "",
      },
    };
  },
  async created() {
    this.isLoading = true;
    try {
      await this.getUsers();
    } catch (error) {
      this.toast.error(ErrorMessageFormatter(error));
    } finally {
      this.isLoading = false;
    }
  },
  methods: {
    async handleSubmit() {
      this.workOrder.companyId = this.userStore.activeCompanyId;
      this.isSubmitLoading = true;
      try {
        const createdWorkOrderId = await this.workOrderService.createWorkOrder(this.workOrder);
        this.toast.success((this as any).$t("General.CreatedSuccessfully"));
        this.$emit("toggle-modal");
        await this.refreshData();
      } catch (error) {
        this.toast.error(ErrorMessageFormatter(error));
      } finally {
        this.isSubmitLoading = false;
      }
    },

    async getUsers() {
      this.isUserListLoading = true;
      try {
        const response = await this.userService.getAllUsers(1, 100000);
        this.users = response.items;
      } catch (error) {
        this.toast.error(ErrorMessageFormatter(error));
      } finally {
        this.isUserListLoading = false;
      }
    },
  },
});
</script>

<style scoped></style>
